import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import authFetch from "../../authFetch";
import PlatformCtx from "../../state/PlatformCtx";
import { TODAY, fmtSAR, fmtDate, fmtDateRange } from "../../utils/format";
import { uploadFileToStorage, authFileDownload } from "../../utils/upload";
import { ExpenseForm } from "../finance/expenses/ExpensesModule";
import { useValidationToast, validateFields } from "../../utils/validation";
import { projectCan, getEffectiveTaxonomy } from "../../permissions/checks";
import { fireNotification } from "../../notifications";
import Modal from "../../shared/Modal";
import TranslateInline from "../../shared/TranslateInline";
import useBnavFab from "../../shared/useBnavFab";
import TaskDetailModal from "../tasks/TaskDetailModal";
import { canEditTask } from "../../permissions/checks";

/* ══ PROJECTS / INITIATIVES ════════════════════════════════
   Linking layer — tasks and expenses reference a projectId.
   The project groups records without duplicating them.
   v2: RBAC, search/filter, i18n, edit, delete, budget,
       notifications, mobile layout, collision-safe IDs.
══════════════════════════════════════════════════════════ */

const PROJECT_STATUS_COLORS = {
  active:    {pill:"p-done",  label:"Active",    labelAr:"نشط"},
  on_hold:   {pill:"p-wait",  label:"On Hold",   labelAr:"معلق"},
  completed: {pill:"p-low",   label:"Completed", labelAr:"مكتمل"},
};

function _projId(prefix="proj"){
  const uuid = (typeof crypto!=="undefined"&&crypto.randomUUID) ? crypto.randomUUID().replace(/-/g,"").slice(0,12)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2,8)}`;
  return `${prefix}_${uuid}`;
}
function _getAPI(){return typeof window!=="undefined"?window.location.origin:"";}

/* Humanize role keys like "staff_driver" → "Staff - Driver", "family_manager" → "Family - Manager". */
function _fmtRole(r){
  if(!r||typeof r!=="string")return "";
  return r.split("_").map(s=>s?s[0].toUpperCase()+s.slice(1):s).join(" - ");
}

function ProjectsModule({state, dispatch, t, currentUser}) {
  const platformCtx = useContext(PlatformCtx);
  const [selected, setSelected] = useState(null);
  const projects = state.projects || [];
  const isRTL = state.lang === "ar";
  const canView = projectCan("viewProjects",currentUser,state,platformCtx);

  /* Filter projects by membership: owner sees all; other users only see
     projects with no user-members (open) or projects they belong to. */
  const visibleProjects = useMemo(() => {
    const live = projects.filter(p => !p.deletedAt);
    if (currentUser?.role === "owner") return live;
    return live.filter(p => {
      const userMembers = (p.people||[]).filter(pp => pp.type === "user");
      return userMembers.length === 0 || userMembers.some(pp => pp.userId === currentUser?.id);
    });
  }, [projects, currentUser?.role, currentUser?.id]);

  const selectedProject = selected ? visibleProjects.find(p => p.id === selected) : null;
  useEffect(() => { if (selected && !selectedProject) setSelected(null); }, [selected, selectedProject]);

  if (!canView) return <div className="page"><div className="empty">{isRTL?"لا توجد صلاحية للوصول":"Access restricted."}</div></div>;

  return (
    <>
      <ProjectList projects={visibleProjects} state={state} dispatch={dispatch} t={t} onOpen={setSelected} subtitle={state.moduleSubtitles?.Projects} platformName={state.platformModuleNames?.Projects} currentUser={currentUser}/>
      {selectedProject && (
        <Modal>
          <div className="stf-detail-overlay" onClick={()=>setSelected(null)} role="dialog" aria-modal="true" aria-label={isRTL?"تفاصيل المشروع":"Project details"}>
            <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
              <div className="stf-detail-handle" aria-hidden="true"/>
              <ProjectDetail project={selectedProject} state={state} dispatch={dispatch} t={t} onBack={()=>setSelected(null)} currentUser={currentUser} inSheet/>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function ProjectList({projects, state, dispatch, t, onOpen, subtitle, platformName, currentUser}) {
  const platformCtx = useContext(PlatformCtx);
  const canAdd = projectCan("addProject",currentUser,state,platformCtx);
  const isRTL = state.lang === "ar";
  const vt=useValidationToast();
  const [showAdd, setShowAdd] = useState(false);useBnavFab(()=>setShowAdd(true));
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [form, setForm] = useState({name:"",nameAr:"",description:"",descriptionAr:"",status:"active",startDate:TODAY,endDate:"",budget:""});
  const sf = (k,v) => setForm(p=>({...p,[k]:v}));

  let filtered = projects;
  if (search) { const q = search.trim().toLowerCase(); filtered = filtered.filter(p => p.name.toLowerCase().includes(q) || (p.nameAr||"").toLowerCase().includes(q) || (p.description||"").toLowerCase().includes(q) || (p.descriptionAr||"").toLowerCase().includes(q)); }
  if (filterStatus) filtered = filtered.filter(p => p.status === filterStatus);

  const byStatus = {
    active:    filtered.filter(p=>p.status==="active"),
    on_hold:   filtered.filter(p=>p.status==="on_hold"),
    completed: filtered.filter(p=>p.status==="completed"),
  };

  const allTasks = state.tasks || [];
  const tasksByProject = useMemo(() => {
    const map = {};
    allTasks.filter(tk => !tk.deletedAt).forEach(tk => {
      (tk.linkedEntities||[]).forEach(le => {
        if (le.type === "project" && le.id) {
          if (!map[le.id]) map[le.id] = [];
          map[le.id].push(tk);
        }
      });
    });
    return map;
  }, [allTasks]);

  const [mobileStatus, setMobileStatus] = useState("all");
  const mobileFiltered = mobileStatus === "all" ? filtered : filtered.filter(p => p.status === mobileStatus);
  const countActive = projects.filter(p=>p.status==="active").length;
  const countHold = projects.filter(p=>p.status==="on_hold").length;
  const countDone = projects.filter(p=>p.status==="completed").length;

  return (
    <div className="page">
      {vt.Toast}
      {/* ─── Mobile hero header ─── */}
      <div className="prj-hero">
        <div className="prj-hero-greeting">{platformName||t.projects||"Projects"}</div>
        <div className="prj-hero-sub">{subtitle||(isRTL?"المبادرات · ربط المهام والمصروفات والأشخاص":"Initiatives · Linking tasks, expenses, people")}</div>

        {/* Summary glass card */}
        <div className="prj-summary-glass">
          <div className="prj-summary-row">
            <div className="prj-summary-item"><div className="prj-summary-val" style={{color:"var(--ink)"}}>{projects.length}</div><div className="prj-summary-lbl">{isRTL?"الكل":"Total"}</div></div>
            <div className="prj-summary-item"><div className="prj-summary-val" style={{color:"var(--grn)"}}>{countActive}</div><div className="prj-summary-lbl">{isRTL?"نشط":"Active"}</div></div>
            <div className="prj-summary-item"><div className="prj-summary-val" style={{color:"var(--blu)"}}>{countHold}</div><div className="prj-summary-lbl">{isRTL?"معلق":"On Hold"}</div></div>
            <div className="prj-summary-item"><div className="prj-summary-val" style={{color:"var(--g400)"}}>{countDone}</div><div className="prj-summary-lbl">{isRTL?"مكتمل":"Done"}</div></div>
          </div>
        </div>
      </div>

      {/* ─── Mobile search ─── */}
      <div className="prj-search-mobile">
        <input className="fi" value={search} onChange={e=>setSearch(e.target.value)} placeholder={t.searchProjects||"Search projects…"}/>
      </div>

      {/* ─── Mobile status tabs ─── */}
      <div className="prj-status-tabs">
        {[{k:"all",l:isRTL?"الكل":"All"},{k:"active",l:isRTL?"نشط":"Active"},{k:"on_hold",l:isRTL?"معلق":"On Hold"},{k:"completed",l:isRTL?"مكتمل":"Completed"}].map(tb=>(
          <button key={tb.k} className={`prj-status-tab ${mobileStatus===tb.k?"active":""}`} onClick={()=>setMobileStatus(tb.k)}>{tb.l}</button>
        ))}
      </div>

      {/* ─── Mobile project list ─── */}
      <div className="prj-mobile-list">
        {mobileFiltered.length === 0 ? (
          <div className="prj-merged-card" style={{padding:32,textAlign:"center"}}>
            <div style={{fontSize:14,color:"var(--g400)",marginBottom:8}}>{t.noProjectsYet||"No projects yet"}</div>
            <div style={{fontSize:12,color:"var(--g400)"}}>{t.createProjectHint||"Create a project to group tasks, expenses, and people in one place."}</div>
          </div>
        ) : (
          ["active","on_hold","completed"].map(status => {
            const list = mobileFiltered.filter(p=>p.status===status);
            if (!list.length) return null;
            const sc = PROJECT_STATUS_COLORS[status];
            const iconBg = "var(--primary,var(--ink))";
            return (
              <div key={status}>
                <div className="prj-section-label">{isRTL ? sc.labelAr : sc.label} ({list.length})</div>
                <div className="prj-merged-card">
                  {list.map(p => {
                    const pTasks = tasksByProject[p.id]||[];
                    const pDone = pTasks.filter(tk=>tk.status==="completed").length;
                    const pTotal = pTasks.length;
                    const pPct = pTotal ? Math.round(pDone/pTotal*100) : null;
                    const displayName = isRTL && p.nameAr ? p.nameAr : p.name;
                    const pBudget = parseFloat(p.budget)||0;
                    const initials = displayName.slice(0,2).toUpperCase();
                    return (
                      <div key={p.id} className="prj-row" onClick={()=>onOpen(p.id)}>
                        <div className="prj-row-icon" style={{background:iconBg}}>{initials}</div>
                        <div className="prj-row-info">
                          <div className="prj-row-name">{displayName}</div>
                          <div className="prj-row-sub">
                            {fmtDate(p.startDate,state.lang)}{pBudget>0?` · ${fmtSAR(pBudget)}`:""}{pTotal>0?` · ${pDone}/${pTotal} ${isRTL?"مهام":"tasks"}`:""}
                          </div>
                        </div>
                        <div className="prj-row-right">
                          {pPct !== null && (
                            <>
                              <span style={{fontFamily:"var(--fm)",fontSize:10,fontWeight:600,color:pPct===100?"var(--grn)":"var(--g500)"}}>{pPct}%</span>
                              <div className="prj-row-progress" role="progressbar" aria-valuenow={pPct} aria-valuemin={0} aria-valuemax={100} aria-label={isRTL?"تقدم المشروع":"Project progress"}><div className="prj-row-progress-fill" style={{width:`${pPct}%`,background:pPct===100?"var(--grn)":"var(--primary,var(--ink))"}}/></div>
                            </>
                          )}
                          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" stroke="var(--g400)" strokeWidth="1.5" strokeLinecap="round"><path d="M1 1l5 5-5 5"/></svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ─── Mobile FAB ─── */}
      {canAdd && <button className="prj-fab" onClick={()=>setShowAdd(true)} aria-label={t.newProject||"New Project"}>+</button>}

      {/* ─── Desktop header ─── */}
      <div className="prj-desktop-section">
        <div className="ph" style={{display:"flex",flexWrap:"wrap",gap:10,alignItems:"center"}}>
          <div style={{flex:1,minWidth:200}}><div className="ptitle">{platformName||t.projects||"Projects"}</div><div className="psub">{subtitle||(isRTL?"المبادرات · ربط المهام والمصروفات والأشخاص":"Initiatives · Linking tasks, expenses, people")}</div></div>
          {canAdd && <button className="btn btn-p" onClick={()=>setShowAdd(true)}>{t.newProject||"New Project"}</button>}
        </div>

        {/* Search + Filter bar */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
          <input className="fi" value={search} onChange={e=>setSearch(e.target.value)} placeholder={t.searchProjects||"Search projects…"} style={{flex:2,minWidth:180,maxWidth:360}}/>
          <select className="fs" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{flex:1,minWidth:140,maxWidth:200}}>
            <option value="">{t.allStatuses||"All Statuses"}</option>
            <option value="active">{t.active||"Active"}</option>
            <option value="on_hold">{t.onHold||"On Hold"}</option>
            <option value="completed">{t.completed||"Completed"}</option>
          </select>
        </div>
      </div>

      {showAdd && (
        <Modal>
          <div className="stf-detail-overlay" onClick={()=>setShowAdd(false)} role="dialog" aria-modal="true" aria-label={t.newProject||"New Project"}>
            <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
              <div className="stf-detail-handle" aria-hidden="true"/>
              <div className="stf-detail-header">
                <div>
                  <div className="stf-detail-title">{t.newProject||"New Project"}</div>
                  <div className="stf-detail-sub">{isRTL?"ابدأ مبادرة جديدة لربط المهام والمصروفات والأشخاص":"Start a new initiative to group tasks, expenses, and people"}</div>
                </div>
                <button className="stf-detail-close" onClick={()=>setShowAdd(false)} aria-label={t.close||"Close"}>✕</button>
              </div>
              <div className="stf-detail-body">
                <div className="fg" style={{marginBottom:12}}>
                  <label className="fl" htmlFor="prj-new-name">{t.projectName||"Project Name"}</label>
                  <input id="prj-new-name" className="fi" value={form.name} onChange={e=>sf("name",e.target.value)} placeholder={isRTL?"مثال: تطوير منزل جدة":"e.g. Jeddah Home Development"} autoFocus/>
                </div>
                {!(isRTL || state.lang === "ar") && (
                  <div className="fg" style={{marginBottom:12}}>
                    <label className="fl" htmlFor="prj-new-name-ar">{t.projectNameAr||"Project Name (Arabic)"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label>
                    <input id="prj-new-name-ar" className="fi" value={form.nameAr} onChange={e=>sf("nameAr",e.target.value)} dir="rtl"/>
                  </div>
                )}
                <div className="fg" style={{marginBottom:12}}>
                  <label className="fl" htmlFor="prj-new-desc">{t.description||"Description"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label>
                  <textarea id="prj-new-desc" className="fta" value={form.description} onChange={e=>sf("description",e.target.value)} placeholder={isRTL?"ما هو هذا المشروع؟":"What is this project about?"} style={{minHeight:60}}/>
                </div>
                <div className="fr">
                  <div className="fg" style={{flex:1}}>
                    <label className="fl" htmlFor="prj-new-status">{t.status||"Status"}</label>
                    <select id="prj-new-status" className="fs" value={form.status} onChange={e=>sf("status",e.target.value)}>
                      <option value="active">{t.active||"Active"}</option>
                      <option value="on_hold">{t.onHold||"On Hold"}</option>
                      <option value="completed">{t.completed||"Completed"}</option>
                    </select>
                  </div>
                  <div className="fg" style={{flex:1}}>
                    <label className="fl" htmlFor="prj-new-start">{t.startDate||"Start Date"}</label>
                    <input id="prj-new-start" className="fi" type="date" value={form.startDate} onChange={e=>sf("startDate",e.target.value)}/>
                  </div>
                </div>
                <div className="fr">
                  <div className="fg" style={{flex:1}}>
                    <label className="fl" htmlFor="prj-new-end">{t.endDate||"End Date"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"opt."})</span></label>
                    <input id="prj-new-end" className="fi" type="date" value={form.endDate} onChange={e=>sf("endDate",e.target.value)} min={form.startDate}/>
                  </div>
                  <div className="fg" style={{flex:1}}>
                    <label className="fl" htmlFor="prj-new-budget">{t.budget||"Budget (SAR)"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label>
                    <input id="prj-new-budget" className="fi" type="number" min="0" step="100" value={form.budget} onChange={e=>sf("budget",e.target.value)} placeholder="0"/>
                  </div>
                </div>
              </div>
              <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowAdd(false)}>{t.cancel||"Cancel"}</button>
                <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{
                  const m=validateFields([{ok:form.name.trim(),label:isRTL?"الاسم":"Name"}]);
                  if(m.length){vt.show(m.join(", "));return;}
                  const budget=parseFloat(form.budget)||0;
                  if(budget<0){vt.show(isRTL?"الميزانية يجب أن تكون موجبة":"Budget must be positive");return;}
                  if(form.endDate&&form.startDate&&form.endDate<form.startDate){vt.show(isRTL?"تاريخ النهاية قبل البداية":"End date is before start date");return;}
                  const proj={...form,budget,id:_projId(),people:[],docs:[],createdAt:TODAY,createdBy:currentUser?.id||"u1"};
                  dispatch({type:"ADD_PROJECT",project:proj});
                  fireNotification({eventKey:"project_created",state,dispatch,platformCtx,link:"Projects",eventData:{projectName:form.name}});
                  setShowAdd(false);setForm({name:"",nameAr:"",description:"",descriptionAr:"",status:"active",startDate:TODAY,endDate:"",budget:""});
                }}>{isRTL?"إنشاء":"Create Project"}</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <div className="prj-desktop-section">
        {filtered.length === 0 && (
          <div className="card"><div className="empty">
            <div style={{fontSize:14,color:"var(--g400)",marginBottom:8}}>{t.noProjectsYet||"No projects yet"}</div>
            <div style={{fontSize:12,color:"var(--g400)"}}>{t.createProjectHint||"Create a project to group tasks, expenses, and people in one place."}</div>
          </div></div>
        )}

        {["active","on_hold","completed"].map(status => {
          const list = byStatus[status];
          if (!list.length) return null;
          const sc = PROJECT_STATUS_COLORS[status];
          return (
            <div key={status} style={{marginBottom:24}}>
              <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:10}}>
                {isRTL ? sc.labelAr : sc.label} ({list.length})
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
                {list.map(p => <ProjectCard key={p.id} project={p} onOpen={onOpen} tasks={tasksByProject[p.id]||[]} isRTL={isRTL}/>)}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}

function ProjectCard({project, onOpen, tasks, isRTL}) {
  const sc = PROJECT_STATUS_COLORS[project.status] || PROJECT_STATUS_COLORS.active;
  const lang = isRTL ? "ar" : "en";
  const duration = project.endDate
    ? fmtDateRange(project.startDate, project.endDate, lang)
    : `${isRTL?"بدأ":"Started"} ${fmtDate(project.startDate, lang)}`;
  const done = tasks.filter(tk=>tk.status==="completed").length;
  const total = tasks.length;
  const pct = total ? Math.round(done/total*100) : null;
  const displayName = isRTL && project.nameAr ? project.nameAr : project.name;
  const displayDesc = isRTL && project.descriptionAr ? project.descriptionAr : project.description;
  const budget = parseFloat(project.budget) || 0;

  return (
    <div className="card" style={{cursor:"pointer",transition:"box-shadow .12s"}}
      onClick={()=>onOpen(project.id)}
      onMouseEnter={e=>e.currentTarget.style.boxShadow="var(--sh2)"}
      onMouseLeave={e=>e.currentTarget.style.boxShadow="var(--sh0)"}>
      <div style={{padding:"16px 18px 14px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:8}}>
          <div style={{fontSize:15,fontWeight:600,color:"var(--ink)",letterSpacing:"-.01em",lineHeight:1.3,flex:1}}>{displayName}</div>
          <span className={`pill ${sc.pill}`} style={{flexShrink:0}}>{isRTL?sc.labelAr:sc.label}</span>
        </div>
        {displayDesc && (
          <div style={{fontSize:12,color:"var(--g500)",lineHeight:1.6,marginBottom:10,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{displayDesc}</div>
        )}
        <div style={{fontFamily:"var(--fm)",fontSize:10,color:"var(--g400)",marginBottom:8}}>{duration}{budget>0?` · ${isRTL?"ميزانية":"Budget"}: ${fmtSAR(budget)}`:""}</div>
        {total > 0 && (
          <div style={{marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <span style={{fontSize:11,color:"var(--g400)"}}>{isRTL?"التقدم":"Progress"}</span>
              <span style={{fontSize:11,fontFamily:"var(--fm)",color:pct===100?"var(--grn)":"var(--g500)"}}>{done}/{total} · {pct}%</span>
            </div>
            <div style={{height:4,background:"var(--g100)",borderRadius:2,overflow:"hidden"}} role="progressbar" aria-valuenow={pct||0} aria-valuemin={0} aria-valuemax={100} aria-label={isRTL?"تقدم المشروع":"Project progress"}>
              <div style={{height:"100%",width:`${pct}%`,background:pct===100?"var(--grn)":"var(--primary,var(--ink))",borderRadius:2,transition:"width .3s"}}/>
            </div>
          </div>
        )}
        {(project.people||[]).length > 0 && (
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {(project.people||[]).slice(0,3).map(pp=>(
              <div key={pp.id} style={{background:"var(--g50)",border:"1px solid var(--g200)",borderRadius:20,padding:"2px 9px",fontSize:11,color:"var(--g600)",fontWeight:500}}>{pp.name}</div>
            ))}
            {(project.people||[]).length > 3 && (
              <div style={{background:"var(--g50)",border:"1px solid var(--g200)",borderRadius:20,padding:"2px 9px",fontSize:11,color:"var(--g400)"}}>+{(project.people||[]).length-3}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectDetail({project, state, dispatch, t, onBack, currentUser}) {
  const platformCtx = useContext(PlatformCtx);
  const isRTL = state.lang === "ar";
  const canEdit = projectCan("editProject",currentUser,state,platformCtx,project);
  const canDelete = projectCan("deleteProject",currentUser,state,platformCtx,project);
  const [tab, setTab] = useState("overview");
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [personMode, setPersonMode] = useState("user"); // "user" | "external"
  const [personForm, setPersonForm] = useState({name:"",role:"",userId:""});
  const [showAddWorkstream, setShowAddWorkstream] = useState(false);
  const [wsForm, setWsForm] = useState({name:"",nameAr:"",status:"active",startDate:project.startDate||TODAY,endDate:"",budget:""});
  const wsf = (k,v) => setWsForm(p=>({...p,[k]:v}));
  const [editWsId, setEditWsId] = useState(null);
  const [editWsForm, setEditWsForm] = useState({});
  const [wsFilter, setWsFilter] = useState("");
  const [editStatus, setEditStatus] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [editForm, setEditForm] = useState({name:project.name,nameAr:project.nameAr||"",description:project.description||"",descriptionAr:project.descriptionAr||"",startDate:project.startDate,endDate:project.endDate||"",budget:project.budget||""});

  // Add task from project
  const [showAddTask, setShowAddTask] = useState(false);
  /* Projects taxonomy — Phase 2.2 per refactor-plan-task-taxonomy.md.
     Single-level (root entries only). Legacy taskForm.type stays
     populated from the entry's value for backward compat with any
     reducer/filter that reads task.task_type directly. */
  const projTaxonomy = getEffectiveTaxonomy(state, platformCtx, "Projects");
  const projTaxEnabled = projTaxonomy.length > 0;
  const projTaxLbl = (e) => !e ? "" : (isRTL && e.labelAr ? e.labelAr : e.label);
  const _projDefaultType = projTaxonomy[0] || null;
  const [taskForm, setTaskForm] = useState({title:"",type:_projDefaultType?_projDefaultType.value:"general",typeId:_projDefaultType?_projDefaultType.id:"",status:"Open",priority:"Med",due:"",hat:"",assignMode:"user",assigneeUserId:"",person:"",notes:"",workstreamId:""});
  const stf = (k,v) => setTaskForm(p=>({...p,[k]:v}));

  // Add expense from project
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseWsId, setExpenseWsId] = useState("");

  // Generic confirm modal (replaces window.confirm)
  const [confirmState, setConfirmState] = useState(null); // {title, message, confirmLabel, onConfirm}
  const askConfirm = (opts) => setConfirmState(opts);

  // Task detail sheet (reuses TaskEngine's full-featured detail view)
  const [detailTask, setDetailTask] = useState(null);

  // Upload document to project
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docName, setDocName] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [docTag, setDocTag] = useState("");
  const [docTagSuggestions, setDocTagSuggestions] = useState([]);
  const [showDocTagDrop, setShowDocTagDrop] = useState(false);
  const docInputRef = useRef(null);

  const {expenses, expenseCats, users, hats, locations, lang} = state;
  const vt=useValidationToast();
  const displayName = isRTL && project.nameAr ? project.nameAr : project.name;
  const API = _getAPI();
  const projConfig = state.projectConfig || {};

  const allTasks = state.tasks || [];
  const projectTasks = useMemo(() => allTasks.filter(tk => !tk.deletedAt && (tk.linkedEntities||[]).some(le => le.type === "project" && le.id === project.id)).map(tk => ({
    id: tk.id, title: tk.title, titleAr: tk.titleAr || "",
    status: tk.status === "completed" ? "Done" : tk.status === "blocked" ? "Waiting" : "Open",
    priority: tk.priority === "high" || tk.priority === "urgent" ? "High" : tk.priority === "low" ? "Low" : "Med",
    hat: (tk.contextPayload||{}).hat || "", due: tk.dueDate || "", person: tk.assignedToName || "",
    notes: tk.description || "", taskType: tk.taskType || "", assignedTo: tk.assignedTo || "",
    slaExpectedCompletion: tk.slaExpectedCompletion, slaCompletedAt: tk.slaCompletedAt,
    _raw: tk,
  })), [allTasks, project.id]);
  const projectExpenses = useMemo(() => expenses.filter(e => e.projectId === project.id), [expenses, project.id]);

  const expenseDocs = projectExpenses
    .filter(e => e.invoiceName)
    .map(e => ({id:`edoc_${e.id}`, name:e.invoiceName, source:"Expense", sourceLabel:e.notes||"Expense", date:e.date, url:e.invoiceUrl||null, tag:"Invoice"}));
  const uploadedDocs = (project.docs||[]);
  const projectDocs = [...uploadedDocs, ...expenseDocs];

  const totalCost     = projectExpenses.reduce((a,e) => a+e.amount, 0);
  const doneTasks     = projectTasks.filter(l => l.status === "Done").length;
  const openTasks     = projectTasks.filter(l => l.status !== "Done").length;
  const sc = PROJECT_STATUS_COLORS[project.status] || PROJECT_STATUS_COLORS.active;
  const budget = parseFloat(project.budget) || 0;
  const budgetUsedPct = budget > 0 ? Math.round((totalCost / budget) * 100) : null;

  const duration = project.endDate
    ? fmtDateRange(project.startDate, project.endDate, lang)
    : `${isRTL?"بدأ":"Started"} ${fmtDate(project.startDate, lang)}`;

  const handleDelete = () => {
    dispatch({type:"REMOVE_PROJECT",id:project.id});
    fireNotification({eventKey:"project_deleted",state,dispatch,platformCtx,link:"Projects",eventData:{projectName:project.name}});
    onBack();
  };

  const handleSaveEdit = () => {
    const m=validateFields([{ok:editForm.name.trim(),label:isRTL?"الاسم":"Name"}]);
    if(m.length){vt.show(m.join(", "));return;}
    const budget=parseFloat(editForm.budget)||0;
    if(budget<0){vt.show(isRTL?"الميزانية يجب أن تكون موجبة":"Budget must be positive");return;}
    if(editForm.endDate&&editForm.startDate&&editForm.endDate<editForm.startDate){vt.show(isRTL?"تاريخ النهاية قبل البداية":"End date is before start date");return;}
    dispatch({type:"UPDATE_PROJECT",id:project.id,actorId:currentUser?.id,patch:{
      name:editForm.name.trim(),nameAr:editForm.nameAr.trim(),
      description:editForm.description.trim(),descriptionAr:editForm.descriptionAr.trim(),
      startDate:editForm.startDate,endDate:editForm.endDate,
      budget,
    }});
    setShowEditProject(false);
  };

  const tabs = [
    {k:"overview",     l:isRTL?"نظرة":"Overview"},
    {k:"workstreams",  l:`${isRTL?"مسارات":"Workstreams"}${(project.workstreams||[]).length?` (${(project.workstreams||[]).length})`:""}`},
    {k:"tasks",        l:`${isRTL?"المهام":"Tasks"}${projectTasks.length?` (${projectTasks.length})`:""}`},
    {k:"expenses",     l:`${isRTL?"المصروفات":"Expenses"}${projectExpenses.length?` (${projectExpenses.length})`:""}`},
    {k:"documents",    l:`${isRTL?"المستندات":"Docs"}${projectDocs.length?` (${projectDocs.length})`:""}`},
    {k:"people",       l:`${isRTL?"الأشخاص":"People"}${(project.people||[]).length?` (${(project.people||[]).length})`:""}`},
  ];

  return (
    <>
      {vt.Toast}

      {/* ─── Sheet header ─── */}
      <div className="stf-detail-header">
        <div style={{minWidth:0,flex:1}}>
          <div className="stf-detail-title">{displayName}</div>
          <div className="stf-detail-sub">{duration}{budget>0?` · ${fmtSAR(totalCost)} / ${fmtSAR(budget)}`:""}</div>
        </div>
        <button className="stf-detail-close" onClick={onBack} aria-label={isRTL?"إغلاق":"Close"}>✕</button>
      </div>

      {/* ─── Status pills + alerts + actions ─── */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",padding:"0 20px 12px"}}>
        {canEdit && editStatus ? (
          <>
            <select className="fs" style={{width:"auto",fontSize:13,borderRadius:12}} defaultValue={project.status}
              onChange={e=>{dispatch({type:"UPDATE_PROJECT",id:project.id,actorId:currentUser?.id,patch:{status:e.target.value}});
                fireNotification({eventKey:"project_status_changed",state,dispatch,platformCtx,link:"Projects",eventData:{projectName:project.name,newStatus:e.target.value}});
              }}>
              <option value="active">{t.active||"Active"}</option>
              <option value="on_hold">{t.onHold||"On Hold"}</option>
              <option value="completed">{t.completed||"Completed"}</option>
            </select>
            <button className="btn btn-g btn-sm" onClick={()=>setEditStatus(false)}>{isRTL?"تم":"Done"}</button>
          </>
        ) : (
          canEdit
            ? <button type="button" className={`pill ${sc.pill}`} style={{cursor:"pointer",border:"none",padding:"3px 10px",font:"inherit",fontSize:11}} onClick={()=>setEditStatus(true)} aria-label={isRTL?"تغيير الحالة":"Change status"}>{isRTL?sc.labelAr:sc.label}</button>
            : <span className={`pill ${sc.pill}`} style={{fontSize:11}}>{isRTL?sc.labelAr:sc.label}</span>
        )}
        {budget>0 && totalCost>budget && <span className="pill p-high" style={{fontSize:11}}>{t.overBudget||"Over Budget"}</span>}
        {projectTasks.length>0 && <span style={{fontSize:11,color:"var(--g400)",fontFamily:"var(--fm)"}}>{doneTasks}/{projectTasks.length} {isRTL?"مهام":"tasks"}</span>}
      </div>

      {/* Shared confirm modal */}
      {confirmState && (
        <Modal>
          <div className="stf-detail-overlay" onClick={()=>setConfirmState(null)} role="dialog" aria-modal="true" aria-label={confirmState.title}>
            <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
              <div className="stf-detail-handle" aria-hidden="true"/>
              <div className="stf-detail-header">
                <div className="stf-detail-title">{confirmState.title}</div>
                <button className="stf-detail-close" onClick={()=>setConfirmState(null)} aria-label={t.close||"Close"}>✕</button>
              </div>
              <div className="stf-detail-body">
                <p style={{fontSize:13,color:"var(--g600)",lineHeight:1.6,margin:0}}>{confirmState.message}</p>
              </div>
              <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setConfirmState(null)}>{t.cancel||"Cancel"}</button>
                <button className="btn btn-d" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--red-bg)",border:"1px solid var(--red-bd)",color:"var(--red)"}} onClick={()=>{const fn=confirmState.onConfirm;setConfirmState(null);fn&&fn();}}>{confirmState.confirmLabel||(t.remove||"Remove")}</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <Modal>
          <div className="stf-detail-overlay" onClick={()=>setShowDeleteConfirm(false)} role="dialog" aria-modal="true" aria-label={t.deleteProject||"Delete Project"}>
            <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
              <div className="stf-detail-handle" aria-hidden="true"/>
              <div className="stf-detail-header">
                <div className="stf-detail-title">{t.deleteProject||"Delete Project"}</div>
                <button className="stf-detail-close" onClick={()=>setShowDeleteConfirm(false)} aria-label={t.close||"Close"}>✕</button>
              </div>
              <div className="stf-detail-body">
                <p style={{fontSize:13,color:"var(--g600)",lineHeight:1.6,margin:0}}>{t.deleteProjectConfirm||"Are you sure you want to delete this project? Linked tasks and expenses will remain but lose their project link."}</p>
              </div>
              <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowDeleteConfirm(false)}>{t.cancel||"Cancel"}</button>
                <button className="btn btn-d" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--red-bg)",border:"1px solid var(--red-bd)",color:"var(--red)"}} onClick={handleDelete}>{t.deleteProject||"Delete"}</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit project modal */}
      {showEditProject && (
        <Modal>
          <div className="stf-detail-overlay" onClick={()=>setShowEditProject(false)} role="dialog" aria-modal="true" aria-label={t.editProject||"Edit Project"}>
            <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
              <div className="stf-detail-handle" aria-hidden="true"/>
              <div className="stf-detail-header">
                <div className="stf-detail-title">{t.editProject||"Edit Project"}</div>
                <button className="stf-detail-close" onClick={()=>setShowEditProject(false)} aria-label={t.close||"Close"}>✕</button>
              </div>
              <div className="stf-detail-body">
                <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="prj-edit-name">{t.projectName||"Project Name"}</label><input id="prj-edit-name" className="fi" value={editForm.name} onChange={e=>setEditForm(p=>({...p,name:e.target.value}))} autoFocus/></div>
                <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="prj-edit-name-ar">{t.projectNameAr||"Project Name (Arabic)"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label><input id="prj-edit-name-ar" className="fi" value={editForm.nameAr} onChange={e=>setEditForm(p=>({...p,nameAr:e.target.value}))} dir="rtl"/></div>
                <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="prj-edit-desc">{t.description||"Description"}</label><textarea id="prj-edit-desc" className="fta" value={editForm.description} onChange={e=>setEditForm(p=>({...p,description:e.target.value}))} style={{minHeight:60}}/></div>
                <div className="fr">
                  <div className="fg" style={{flex:1}}><label className="fl" htmlFor="prj-edit-start">{t.startDate||"Start Date"}</label><input id="prj-edit-start" className="fi" type="date" value={editForm.startDate} onChange={e=>setEditForm(p=>({...p,startDate:e.target.value}))}/></div>
                  <div className="fg" style={{flex:1}}><label className="fl" htmlFor="prj-edit-end">{t.endDate||"End Date"}</label><input id="prj-edit-end" className="fi" type="date" value={editForm.endDate} onChange={e=>setEditForm(p=>({...p,endDate:e.target.value}))} min={editForm.startDate}/></div>
                </div>
                <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="prj-edit-budget">{t.budget||"Budget (SAR)"}</label><input id="prj-edit-budget" className="fi" type="number" min="0" step="100" value={editForm.budget} onChange={e=>setEditForm(p=>({...p,budget:e.target.value}))}/></div>
              </div>
              <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowEditProject(false)}>{t.cancel||"Cancel"}</button>
                <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={handleSaveEdit}>{t.saveChanges||"Save Changes"}</button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ─── Tabs (horizontal scroll) ─── */}
      <div style={{display:"flex",gap:0,overflowX:"auto",padding:"0 20px",marginBottom:12,borderBottom:"1px solid var(--g100)"}}>
        {tabs.map(tb=>(
          <div key={tb.k} onClick={()=>setTab(tb.k)} style={{padding:"8px 12px",fontSize:12,fontWeight:tab===tb.k?600:400,color:tab===tb.k?"var(--ink)":"var(--g400)",borderBottom:tab===tb.k?"2px solid var(--ink)":"2px solid transparent",cursor:"pointer",whiteSpace:"nowrap",transition:"all .15s"}}>{tb.l}</div>
        ))}
      </div>

      <div className="stf-detail-body">
      {/* OVERVIEW */}
      {tab==="overview" && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Budget vs Actual */}
          {budget > 0 && (
            <div className="exp-merged-card" style={{padding:"14px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{t.budgetVsActual||"Budget vs Actual"}</span>
                <span style={{fontSize:13,fontFamily:"var(--fm)",fontWeight:600,color:totalCost>budget?"var(--red)":"var(--grn)"}}>
                  {fmtSAR(totalCost)} / {fmtSAR(budget)} ({budgetUsedPct}%)
                </span>
              </div>
              <div style={{height:6,background:"var(--g100)",borderRadius:4,overflow:"hidden"}} role="progressbar" aria-valuenow={budgetUsedPct||0} aria-valuemin={0} aria-valuemax={100} aria-label={t.budgetVsActual||"Budget vs Actual"}>
                <div style={{height:"100%",width:`${Math.min(budgetUsedPct,100)}%`,background:totalCost>budget?"var(--red)":"var(--grn)",borderRadius:4,transition:"width .4s"}}/>
              </div>
              {totalCost > budget && <div style={{marginTop:6,fontSize:11,color:"var(--red)",fontWeight:600}}>{t.overBudget||"Over Budget"}: +{fmtSAR(totalCost - budget)}</div>}
            </div>
          )}

          {/* Task progress with status breakdown */}
          {projectTasks.length > 0 && (
            <div className="exp-merged-card" style={{padding:"14px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{isRTL?"تقدم المهام":"Task Progress"}</span>
                <span style={{fontSize:13,fontFamily:"var(--fm)",fontWeight:600,color:doneTasks===projectTasks.length?"var(--grn)":"var(--g600)"}}>
                  {doneTasks} / {projectTasks.length} · {Math.round(doneTasks/projectTasks.length*100)}%
                </span>
              </div>
              <div style={{height:6,background:"var(--g100)",borderRadius:4,overflow:"hidden"}} role="progressbar" aria-valuenow={Math.round(doneTasks/projectTasks.length*100)} aria-valuemin={0} aria-valuemax={100} aria-label={isRTL?"تقدم المهام":"Task progress"}>
                <div style={{height:"100%",width:`${Math.round(doneTasks/projectTasks.length*100)}%`,background:doneTasks===projectTasks.length?"var(--grn)":"var(--primary,var(--ink))",borderRadius:4,transition:"width .4s"}}/>
              </div>
              <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                {[{s:"Open",sAr:"مفتوح",cls:"p-open"},{s:"Waiting",sAr:"انتظار",cls:"p-wait"},{s:"Done",sAr:"مكتمل",cls:"p-done"}].map(({s,sAr,cls})=>{
                  const c=projectTasks.filter(l=>l.status===s).length;
                  if(!c) return null;
                  return <span key={s} className={`pill ${cls}`} style={{fontSize:11}}>{c} {isRTL?sAr:s}</span>;
                })}
              </div>
            </div>
          )}
          {(isRTL&&project.descriptionAr?project.descriptionAr:project.description) && (
            <div className="exp-merged-card" style={{padding:"14px 16px"}}>
              <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>{isRTL?"حول":"About"}</div>
              <div style={{fontSize:13.5,color:"var(--g700)",lineHeight:1.7}}><TranslateInline text={isRTL&&project.descriptionAr?project.descriptionAr:project.description} lang={lang} domain="project description"/></div>
            </div>
          )}
          <div className="exp-merged-card" style={{padding:"14px 16px"}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>{isRTL?"التقدم":"Progress"}</div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
              <span style={{fontSize:12,color:"var(--g500)"}}>{isRTL?"المهام المكتملة":"Tasks completed"}</span>
              <span style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:600}}>{doneTasks}/{projectTasks.length}</span>
            </div>
            <div style={{height:5,background:"var(--g100)",borderRadius:3,overflow:"hidden",marginBottom:14}} role="progressbar" aria-valuenow={projectTasks.length?Math.round((doneTasks/projectTasks.length)*100):0} aria-valuemin={0} aria-valuemax={100} aria-label={isRTL?"المهام المكتملة":"Tasks completed"}>
              <div style={{height:"100%",width:projectTasks.length?`${(doneTasks/projectTasks.length)*100}%`:"0%",background:"var(--grn)",borderRadius:3}}/>
            </div>
            {[[isRTL?"إجمالي المصروفات":"Total Expenses",fmtSAR(totalCost)],[isRTL?"المستندات":"Documents",projectDocs.length],[isRTL?"الفريق":"Team Size",(project.people||[]).length]].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:"1px solid var(--g100)"}}>
                <span style={{fontSize:12,color:"var(--g500)"}}>{k}</span>
                <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600}}>{v}</span>
              </div>
            ))}
            {budget>0&&(
              <div style={{display:"flex",justifyContent:"space-between",padding:"7px 0"}}>
                <span style={{fontSize:12,color:"var(--g500)"}}>{isRTL?"الميزانية":"Budget"}</span>
                <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,color:totalCost>budget?"var(--red)":"var(--grn)"}}>{fmtSAR(totalCost)} / {fmtSAR(budget)}</span>
              </div>
            )}
            {project.updatedAt&&(()=>{
              const editor=(users||[]).find(u=>u.id===project.updatedBy);
              const name=editor?(isRTL&&editor.nameAr?editor.nameAr:editor.name):(project.updatedBy||"");
              return(
                <div style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderTop:"1px solid var(--g100)"}}>
                  <span style={{fontSize:12,color:"var(--g500)"}}>{isRTL?"آخر تعديل":"Last updated"}</span>
                  <span style={{fontFamily:"var(--fm)",fontSize:11,color:"var(--g500)"}}>{fmtDate(project.updatedAt,lang)}{name?` · ${name}`:""}</span>
                </div>
              );
            })()}
          </div>
          <div className="exp-merged-card">
            <div style={{padding:"12px 16px 6px"}}><div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{isRTL?"آخر المهام":"Recent Tasks"}</div></div>
            {projectTasks.length===0 && <div style={{padding:"20px 16px",textAlign:"center",fontSize:13,color:"var(--g400)"}}>{isRTL?"لا مهام مرتبطة بعد":"No tasks linked yet."}</div>}
            {projectTasks.slice(0,5).map(l=>(
              <div key={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderTop:"1px solid var(--g100)",cursor:"pointer"}}
                   onClick={()=>l._raw&&setDetailTask(l._raw)}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13.5,fontWeight:500,letterSpacing:"-.01em"}}>{isRTL&&l.titleAr?l.titleAr:l.title}</div>
                  <div style={{fontSize:11,color:"var(--g400)"}}>{l.hat}{l.due?` · ${fmtDate(l.due,lang)}`:""}</div>
                </div>
                <span className={`pill ${l.status==="Done"?"p-done":l.status==="Waiting"?"p-wait":"p-open"}`}>{l.status==="Done"?t.done:l.status==="Waiting"?t.waiting:t.open}</span>
              </div>
            ))}
          </div>
          {(project.people||[]).length>0 && (
            <div className="exp-merged-card">
              <div style={{padding:"12px 16px 6px"}}><div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em"}}>{isRTL?"الفريق":"Team"}</div></div>
              {(project.people||[]).map(pp=>(
                <div key={pp.id} style={{padding:"9px 16px",borderTop:"1px solid var(--g100)",display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:"var(--g100)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--fm)",fontSize:10,fontWeight:600,color:"var(--g600)",flexShrink:0}}>{pp.name.slice(0,2).toUpperCase()}</div>
                  <div style={{flex:1}}><div style={{fontSize:13,fontWeight:500}}>{pp.name}</div>{pp.role&&<div style={{fontSize:11,color:"var(--g400)"}}>{pp.role}</div>}</div>
                </div>
              ))}
            </div>
          )}

          {/* Edit / Delete actions — bottom of overview */}
          {(canEdit||canDelete)&&(
            <div style={{display:"grid",gridTemplateColumns:canEdit&&canDelete?"1fr 1fr":"1fr",gap:8,marginTop:20}}>
              {canEdit&&<button className="btn btn-g" style={{width:"100%",padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:14}} onClick={()=>setShowEditProject(true)}>{t.editProject||"Edit Project"}</button>}
              {canDelete&&<button className="btn btn-d" style={{width:"100%",padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:14,background:"var(--red-bg)",border:"1px solid var(--red-bd)",color:"var(--red)"}} onClick={()=>setShowDeleteConfirm(true)}>{t.deleteProject||"Delete Project"}</button>}
            </div>
          )}
        </div>
      )}

      {/* WORKSTREAMS */}
      {tab==="workstreams" && (()=>{
        const workstreams = project.workstreams || [];
        const WS_COLORS = {active:{pill:"p-done",label:"Active",labelAr:"نشط"},on_hold:{pill:"p-wait",label:"On Hold",labelAr:"معلق"},completed:{pill:"p-low",label:"Completed",labelAr:"مكتمل"}};
        return(
        <div>

          {showAddWorkstream && (
            <Modal>
              <div className="stf-detail-overlay" onClick={()=>setShowAddWorkstream(false)} role="dialog" aria-modal="true" aria-label={isRTL?"مسار عمل جديد":"New Workstream"}>
                <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
                  <div className="stf-detail-handle" aria-hidden="true"/>
                  <div className="stf-detail-header">
                    <div className="stf-detail-title">{isRTL?"مسار عمل جديد":"New Workstream"}</div>
                    <button className="stf-detail-close" onClick={()=>setShowAddWorkstream(false)} aria-label={t.close||"Close"}>✕</button>
                  </div>
                  <div className="stf-detail-body">
                    <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="ws-new-name">{isRTL?"الاسم":"Name"}</label><input id="ws-new-name" className="fi" value={wsForm.name} onChange={e=>wsf("name",e.target.value)} placeholder={isRTL?"مثال: الأساسات":"e.g. Foundation"} autoFocus/></div>
                    <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="ws-new-name-ar">{isRTL?"الاسم بالعربي":"Name (Arabic)"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label><input id="ws-new-name-ar" className="fi" value={wsForm.nameAr} onChange={e=>wsf("nameAr",e.target.value)} dir="rtl"/></div>
                    <div className="fr">
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-new-status">{t.status||"Status"}</label><select id="ws-new-status" className="fs" value={wsForm.status} onChange={e=>wsf("status",e.target.value)}><option value="active">{t.active||"Active"}</option><option value="on_hold">{t.onHold||"On Hold"}</option><option value="completed">{t.completed||"Completed"}</option></select></div>
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-new-start">{t.startDate||"Start Date"}</label><input id="ws-new-start" className="fi" type="date" value={wsForm.startDate} onChange={e=>wsf("startDate",e.target.value)}/></div>
                    </div>
                    <div className="fr">
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-new-end">{t.endDate||"End Date"}</label><input id="ws-new-end" className="fi" type="date" value={wsForm.endDate} onChange={e=>wsf("endDate",e.target.value)} min={wsForm.startDate}/></div>
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-new-budget">{t.budget||"Budget (SAR)"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label><input id="ws-new-budget" className="fi" type="number" min="0" step="100" value={wsForm.budget} onChange={e=>wsf("budget",e.target.value)}/></div>
                    </div>
                  </div>
                  <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                    <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowAddWorkstream(false)}>{t.cancel||"Cancel"}</button>
                    <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{
                      const m=validateFields([{ok:wsForm.name.trim(),label:isRTL?"الاسم":"Name"}]);
                      if(m.length){vt.show(m.join(", "));return;}
                      const budget=parseFloat(wsForm.budget)||0;
                      if(budget<0){vt.show(isRTL?"الميزانية يجب أن تكون موجبة":"Budget must be positive");return;}
                      dispatch({type:"ADD_PROJECT_WORKSTREAM",projectId:project.id,workstream:{id:_projId("ws"),name:wsForm.name.trim(),nameAr:wsForm.nameAr.trim(),status:wsForm.status||"active",startDate:wsForm.startDate,endDate:wsForm.endDate,budget}});
                      setWsForm({name:"",nameAr:"",status:"active",startDate:project.startDate||TODAY,endDate:"",budget:""});
                      setShowAddWorkstream(false);
                    }}>{isRTL?"إنشاء":"Create"}</button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {/* Edit workstream modal */}
          {editWsId && (()=>{
            const ws=workstreams.find(w=>w.id===editWsId);
            if(!ws)return null;
            return(
              <Modal>
                <div className="stf-detail-overlay" onClick={()=>setEditWsId(null)} role="dialog" aria-modal="true" aria-label={isRTL?"تعديل مسار العمل":"Edit Workstream"}>
                  <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
                    <div className="stf-detail-handle" aria-hidden="true"/>
                    <div className="stf-detail-header">
                      <div className="stf-detail-title">{isRTL?"تعديل مسار العمل":"Edit Workstream"}</div>
                      <button className="stf-detail-close" onClick={()=>setEditWsId(null)} aria-label={t.close||"Close"}>✕</button>
                    </div>
                    <div className="stf-detail-body">
                      <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="ws-edit-name">{isRTL?"الاسم":"Name"}</label><input id="ws-edit-name" className="fi" value={editWsForm.name||""} onChange={e=>setEditWsForm(p=>({...p,name:e.target.value}))} autoFocus/></div>
                      <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="ws-edit-name-ar">{isRTL?"الاسم بالعربي":"Name (Arabic)"}</label><input id="ws-edit-name-ar" className="fi" value={editWsForm.nameAr||""} onChange={e=>setEditWsForm(p=>({...p,nameAr:e.target.value}))} dir="rtl"/></div>
                      <div className="fr">
                        <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-edit-status">{t.status||"Status"}</label><select id="ws-edit-status" className="fs" value={editWsForm.status||"active"} onChange={e=>setEditWsForm(p=>({...p,status:e.target.value}))}><option value="active">{t.active||"Active"}</option><option value="on_hold">{t.onHold||"On Hold"}</option><option value="completed">{t.completed||"Completed"}</option></select></div>
                        <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-edit-start">{t.startDate||"Start"}</label><input id="ws-edit-start" className="fi" type="date" value={editWsForm.startDate||""} onChange={e=>setEditWsForm(p=>({...p,startDate:e.target.value}))}/></div>
                      </div>
                      <div className="fr">
                        <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-edit-end">{t.endDate||"End"}</label><input id="ws-edit-end" className="fi" type="date" value={editWsForm.endDate||""} onChange={e=>setEditWsForm(p=>({...p,endDate:e.target.value}))} min={editWsForm.startDate}/></div>
                        <div className="fg" style={{flex:1}}><label className="fl" htmlFor="ws-edit-budget">{t.budget||"Budget (SAR)"}</label><input id="ws-edit-budget" className="fi" type="number" min="0" step="100" value={editWsForm.budget||""} onChange={e=>setEditWsForm(p=>({...p,budget:e.target.value}))}/></div>
                      </div>
                    </div>
                    <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                      <button className="btn btn-d" style={{borderRadius:14,padding:"14px 16px",fontSize:13,fontWeight:600,background:"var(--red-bg)",border:"1px solid var(--red-bd)",color:"var(--red)"}} onClick={()=>{
                        const wsId=editWsId;
                        askConfirm({
                          title:isRTL?"حذف مسار العمل":"Delete Workstream",
                          message:isRTL?"سيتم إزالة هذا المسار وفك ارتباط مهامه ومصاريفه.":"This will remove the workstream and unlink its tasks and expenses.",
                          confirmLabel:isRTL?"حذف":"Delete",
                          onConfirm:()=>dispatch({type:"REMOVE_PROJECT_WORKSTREAM",projectId:project.id,workstreamId:wsId}),
                        });
                        setEditWsId(null);
                      }}>{isRTL?"حذف":"Delete"}</button>
                      <div style={{flex:1}}/>
                      <button className="btn btn-g" style={{borderRadius:14,padding:"14px 16px",fontSize:13,fontWeight:600}} onClick={()=>setEditWsId(null)}>{t.cancel||"Cancel"}</button>
                      <button className="btn btn-p" style={{borderRadius:14,padding:"14px 16px",fontSize:13,fontWeight:600}} onClick={()=>{dispatch({type:"UPDATE_PROJECT_WORKSTREAM",projectId:project.id,workstreamId:editWsId,patch:{name:editWsForm.name,nameAr:editWsForm.nameAr,status:editWsForm.status,startDate:editWsForm.startDate,endDate:editWsForm.endDate,budget:parseFloat(editWsForm.budget)||0}});setEditWsId(null);}}>{t.saveChanges||"Save"}</button>
                    </div>
                  </div>
                </div>
              </Modal>
            );
          })()}

          {workstreams.length === 0
            ? <div className="exp-merged-card" style={{padding:32,textAlign:"center"}}>
                <div style={{fontSize:14,color:"var(--g400)",marginBottom:6}}>{isRTL?"لا مسارات عمل بعد":"No workstreams yet"}</div>
                <div style={{fontSize:12,color:"var(--g400)"}}>{isRTL?"أنشئ مسارات عمل لتنظيم المهام ضمن مراحل أو أقسام.":"Create workstreams to organize tasks into phases or tracks."}</div>
              </div>
            : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {workstreams.map(ws=>{
                  const wsTasks=projectTasks.filter(tk=>(tk._raw?.contextPayload||tk).workstreamId===ws.id||(tk._raw?.contextPayload||{}).workstreamId===ws.id);
                  const wsDone=wsTasks.filter(tk=>tk.status==="Done").length;
                  const wsTotal=wsTasks.length;
                  const wsPct=wsTotal?Math.round(wsDone/wsTotal*100):0;
                  const wsExpenses=projectExpenses.filter(e=>(e.workstreamId||"")=== ws.id);
                  const wsCost=wsExpenses.reduce((a,e)=>a+e.amount,0);
                  const wsBudget=parseFloat(ws.budget)||0;
                  const sc=WS_COLORS[ws.status]||WS_COLORS.active;
                  const wsName=isRTL&&ws.nameAr?ws.nameAr:ws.name;
                  const duration=ws.endDate?fmtDateRange(ws.startDate,ws.endDate,lang):`${isRTL?"بدأ":"Started"} ${fmtDate(ws.startDate,lang)}`;
                  return(
                    <div key={ws.id} className="exp-merged-card" style={{cursor:canEdit?"pointer":"default",padding:"14px 16px"}} onClick={()=>{if(canEdit){setEditWsId(ws.id);setEditWsForm({...ws,budget:ws.budget||""});}}}>
                      <div>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                          <div style={{fontSize:15,fontWeight:600,color:"var(--ink)",flex:1}}>{wsName}</div>
                          <span className={`pill ${sc.pill}`} style={{flexShrink:0}}>{isRTL?sc.labelAr:sc.label}</span>
                        </div>
                        <div style={{fontFamily:"var(--fm)",fontSize:10,color:"var(--g400)",marginBottom:10}}>{duration}</div>
                        {/* Task progress */}
                        <div style={{marginBottom:8}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                            <span style={{fontSize:11,color:"var(--g400)"}}>{isRTL?"المهام":"Tasks"}</span>
                            <span style={{fontSize:11,fontFamily:"var(--fm)",color:wsPct===100?"var(--grn)":"var(--g500)"}}>{wsDone}/{wsTotal}{wsTotal>0?` · ${wsPct}%`:""}</span>
                          </div>
                          <div style={{height:4,background:"var(--g100)",borderRadius:2,overflow:"hidden"}} role="progressbar" aria-valuenow={wsPct} aria-valuemin={0} aria-valuemax={100} aria-label={isRTL?"تقدم المهام":"Task progress"}>
                            <div style={{height:"100%",width:`${wsPct}%`,background:wsPct===100?"var(--grn)":"var(--primary,var(--ink))",borderRadius:2,transition:"width .3s"}}/>
                          </div>
                        </div>
                        {/* Budget progress */}
                        {wsBudget>0&&(
                          <div>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                              <span style={{fontSize:11,color:"var(--g400)"}}>{isRTL?"الميزانية":"Budget"}</span>
                              <span style={{fontSize:11,fontFamily:"var(--fm)",color:wsCost>wsBudget?"var(--red)":"var(--g500)"}}>{fmtSAR(wsCost)} / {fmtSAR(wsBudget)}</span>
                            </div>
                            <div style={{height:4,background:"var(--g100)",borderRadius:2,overflow:"hidden"}} role="progressbar" aria-valuenow={Math.min(Math.round(wsCost/wsBudget*100),100)} aria-valuemin={0} aria-valuemax={100} aria-label={isRTL?"استخدام الميزانية":"Budget usage"}>
                              <div style={{height:"100%",width:`${Math.min(Math.round(wsCost/wsBudget*100),100)}%`,background:wsCost>wsBudget?"var(--red)":"var(--grn)",borderRadius:2,transition:"width .3s"}}/>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
          }
          {canEdit&&(
            <div style={{marginTop:20}}>
              <button className="btn btn-p" style={{width:"100%",padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:14}} onClick={()=>setShowAddWorkstream(true)}>+ {isRTL?"مسار عمل":"Workstream"}</button>
            </div>
          )}
        </div>
        );
      })()}

      {/* TASKS */}
      {tab==="tasks" && (
        <div>
          {showAddTask&&(
            <Modal>
              <div className="stf-detail-overlay" onClick={()=>setShowAddTask(false)} role="dialog" aria-modal="true" aria-label={isRTL?"مهمة جديدة":"New Task"}>
                <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
                  <div className="stf-detail-handle" aria-hidden="true"/>
                  <div className="stf-detail-header">
                    <div style={{minWidth:0,flex:1}}>
                      <div className="stf-detail-title">{isRTL?"مهمة جديدة":"New Task"}</div>
                      <div className="stf-detail-sub">{displayName}</div>
                    </div>
                    <button className="stf-detail-close" onClick={()=>setShowAddTask(false)} aria-label={t.close||"Close"}>✕</button>
                  </div>
                  <div className="stf-detail-body">
                    <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="tsk-new-title">{isRTL?"المهمة":"Task"}</label><input id="tsk-new-title" className="fi" value={taskForm.title} onChange={e=>stf("title",e.target.value)} placeholder={isRTL?"ما الذي يجب القيام به؟":"What needs to happen?"} autoFocus/></div>
                    <div className="fg" style={{marginBottom:12}}><label className="fl" htmlFor="tsk-new-ws">{isRTL?"مسار العمل":"Workstream"} <span style={{color:"var(--red)"}}>*</span></label>
                      <select id="tsk-new-ws" className="fs" value={taskForm.workstreamId} onChange={e=>stf("workstreamId",e.target.value)}>
                        <option value="">{isRTL?"— اختر مسار عمل —":"— Select workstream —"}</option>
                        {(project.workstreams||[]).filter(w=>w.status!=="completed").map(w=><option key={w.id} value={w.id}>{isRTL&&w.nameAr?w.nameAr:w.name}</option>)}
                      </select>
                      {(project.workstreams||[]).length===0&&<div style={{fontSize:11,color:"var(--red)",marginTop:4}}>{isRTL?"أنشئ مسار عمل أولاً من تبويب مسارات العمل":"Create a workstream first from the Workstreams tab"}</div>}
                    </div>
                    <div className="fr">
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="tsk-new-type">{t.type||"Type"}</label>
                        {projTaxEnabled?(
                          <select id="tsk-new-type" className="fs" value={taskForm.typeId} onChange={e=>{const id=e.target.value;const ent=projTaxonomy.find(x=>x.id===id);setTaskForm(p=>({...p,typeId:id,type:ent?ent.value:p.type}));}}>{projTaxonomy.map(tt=><option key={tt.id} value={tt.id}>{projTaxLbl(tt)}</option>)}</select>
                        ):(
                          <select id="tsk-new-type" className="fs" value={taskForm.type} onChange={e=>stf("type",e.target.value)}>{(projConfig.taskTypes||["milestone","deliverable","review","general"]).map(tt=><option key={tt} value={tt}>{tt.charAt(0).toUpperCase()+tt.slice(1)}</option>)}</select>
                        )}
                      </div>
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="tsk-new-pri">{t.priority||"Priority"}</label><select id="tsk-new-pri" className="fs" value={taskForm.priority} onChange={e=>stf("priority",e.target.value)}><option value="High">{t.high}</option><option value="Med">{t.med}</option><option value="Low">{t.low}</option></select></div>
                    </div>
                    <div className="fr">
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="tsk-new-hat">{t.hat||"Hat"}</label><select id="tsk-new-hat" className="fs" value={taskForm.hat} onChange={e=>stf("hat",e.target.value)}><option value="">—</option>{hats.map(h=><option key={h}>{h}</option>)}</select></div>
                      <div className="fg" style={{flex:1}}><label className="fl" htmlFor="tsk-new-due">{t.dueDate||"Due Date"}</label><input id="tsk-new-due" className="fi" type="date" value={taskForm.due} onChange={e=>stf("due",e.target.value)}/></div>
                    </div>

                    {/* Assignee with user/external toggle */}
                    <div className="fg" style={{marginBottom:12}}>
                      <label className="fl">{t.assignedTo||(isRTL?"المكلف":"Assignee")}</label>
                      <div style={{display:"flex",gap:6,marginBottom:8}}>
                        <button type="button" className={`btn btn-sm ${taskForm.assignMode==="user"?"btn-p":"btn-g"}`} onClick={()=>stf("assignMode","user")}>{isRTL?"مستخدم":"Workspace User"}</button>
                        <button type="button" className={`btn btn-sm ${taskForm.assignMode==="external"?"btn-p":"btn-g"}`} onClick={()=>stf("assignMode","external")}>{isRTL?"خارجي":"External"}</button>
                      </div>
                      {taskForm.assignMode==="user" ? (
                        <select className="fs" value={taskForm.assigneeUserId} onChange={e=>stf("assigneeUserId",e.target.value)}>
                          <option value="">{isRTL?"— غير مسند —":"— Unassigned —"}</option>
                          {(users||[]).filter(u=>u.active).map(u=><option key={u.id} value={u.id}>{isRTL&&u.nameAr?u.nameAr:u.name}{u.role?` — ${_fmtRole(u.role)}`:""}</option>)}
                        </select>
                      ) : (
                        <input className="fi" value={taskForm.person} onChange={e=>stf("person",e.target.value)} placeholder={isRTL?"اسم جهة خارجية":"External contact name"}/>
                      )}
                    </div>

                    <div className="fg"><label className="fl" htmlFor="tsk-new-notes">{t.notes||"Notes"}</label><textarea id="tsk-new-notes" className="fta" value={taskForm.notes} onChange={e=>stf("notes",e.target.value)} placeholder={isRTL?"سياق، تبعيات…":"Context, dependencies…"} style={{minHeight:56}}/></div>
                  </div>
                  <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                    <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowAddTask(false)}>{t.cancel||"Cancel"}</button>
                    <button className="btn btn-p" disabled={!taskForm.workstreamId||(project.workstreams||[]).length===0} style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",opacity:taskForm.workstreamId?1:0.5}} onClick={async()=>{
                      const m=validateFields([{ok:taskForm.title.trim(),label:isRTL?"العنوان":"Title"},{ok:taskForm.workstreamId,label:isRTL?"مسار العمل":"Workstream"}]);
                      if(m.length){vt.show(m.join(", "));return;}
                      const priMap={High:"high",Med:"medium",Low:"low"};
                      let assignedToId=null, assignedToName="";
                      if(taskForm.assignMode==="user" && taskForm.assigneeUserId){
                        const u=(users||[]).find(x=>x.id===taskForm.assigneeUserId);
                        if(u){assignedToId=u.id; assignedToName=u.name;}
                      } else if(taskForm.assignMode==="external" && taskForm.person.trim()){
                        assignedToName=taskForm.person.trim();
                      }
                      const wsName=(project.workstreams||[]).find(w=>w.id===taskForm.workstreamId)?.name||"";
                      try{
                        const res=await authFetch(`${API}/api/tasks?action=create_task`,{method:"POST",headers:{"Content-Type":"application/json"},
                          body:JSON.stringify({action:"create_task",ws_key:state.wsKey,actor_id:currentUser?.id,actor_name:currentUser?.name,
                            task:{title:taskForm.title.trim(),titleAr:"",description:taskForm.notes||"",
                              priority:priMap[taskForm.priority]||projConfig.defaultPriority||"medium",
                              due_date:taskForm.due||null,created_by:currentUser?.id,
                              assigned_to:assignedToId,assigned_to_name:assignedToName,
                              source_module:"Projects",task_type:taskForm.type||"general",
                              /* Phase 2.2: taxonomy entry id — legacy task_type kept for compat. */
                              task_type_id:taskForm.typeId||null,
                              loc_id:"",
                              context_payload:{projectId:project.id,projectName:project.name,workstreamId:taskForm.workstreamId,workstreamName:wsName,hat:taskForm.hat||hats[0]||"GM",taxonomyId:taskForm.typeId||null},
                              linked_entities:[{type:"project",id:project.id}],
                              requires_approval:false,
                              requires_expense:projConfig.requireExpense||false,
                              approval_roles:[],
                            }})});
                        const data=await res.json();
                        if(data.ok&&data.task){
                          dispatch({type:"ADD_TASK",task:data.task});
                          setTaskForm({title:"",type:"general",status:"Open",priority:"Med",due:"",hat:"",assignMode:"user",assigneeUserId:"",person:"",notes:"",workstreamId:""});
                          setShowAddTask(false);
                        }else{
                          console.error("[ProjectDetail] task engine error:",data);
                          vt.show(isRTL?"فشل إنشاء المهمة. حاول مرة أخرى.":"Could not create task. Please try again.");
                        }
                      }catch(err){
                        console.error("[ProjectDetail] task engine create failed:",err);
                        vt.show(isRTL?"تعذر الاتصال بالخادم":"Network error. Please try again.");
                      }
                    }}>{isRTL?"إنشاء":"Create Task"}</button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {/* Workstream filter */}
          {(project.workstreams||[]).length>1&&(
            <div style={{marginBottom:12}}>
              <select className="fs" value={wsFilter} onChange={e=>setWsFilter(e.target.value)} style={{maxWidth:240}}>
                <option value="">{isRTL?"كل مسارات العمل":"All Workstreams"}</option>
                {(project.workstreams||[]).map(w=><option key={w.id} value={w.id}>{isRTL&&w.nameAr?w.nameAr:w.name}</option>)}
              </select>
            </div>
          )}

          {(()=>{
            const filtered=wsFilter?projectTasks.filter(tk=>(tk._raw?.contextPayload||{}).workstreamId===wsFilter):projectTasks;
            const wsMap=Object.fromEntries((project.workstreams||[]).map(w=>[w.id,isRTL&&w.nameAr?w.nameAr:w.name]));
            return filtered.length===0
            ? <div className="exp-merged-card" style={{padding:32,textAlign:"center"}}>
                <div style={{fontSize:14,color:"var(--g400)",marginBottom:4}}>{isRTL?"لا مهام بعد":"No tasks yet"}</div>
                <div style={{fontSize:12,color:"var(--g400)"}}>{isRTL?"أنشئ مهمة أعلاه":"Create a task above to get started."}</div>
              </div>
            : <div className="exp-merged-card">
                {filtered.map(tk=>{
                  const tkWsId=(tk._raw?.contextPayload||{}).workstreamId||"";
                  return(
                  <div key={tk.id} style={{padding:"12px 16px",borderBottom:"1px solid var(--g100)",display:"flex",alignItems:"center",gap:10,cursor:"pointer",transition:"background .1s"}}
                       onClick={()=>tk._raw&&setDetailTask(tk._raw)}
                       onMouseEnter={e=>e.currentTarget.style.background="var(--g50)"}
                       onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13.5,fontWeight:500,letterSpacing:"-.01em",color:tk.status==="Done"?"var(--g400)":"var(--ink)",textDecoration:tk.status==="Done"?"line-through":"none"}}>{isRTL&&tk.titleAr?tk.titleAr:tk.title}</div>
                      <div style={{fontSize:11,color:"var(--g400)",marginTop:3,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                        {wsMap[tkWsId]&&<span style={{color:"var(--g500)",fontWeight:500}}>{wsMap[tkWsId]}</span>}
                        {wsMap[tkWsId]&&(tk.taskType||tk.priority)&&<span style={{opacity:0.5}}>·</span>}
                        {tk.taskType&&<span style={{textTransform:"capitalize"}}>{tk.taskType}</span>}
                        {tk.taskType&&tk.priority&&<span style={{opacity:0.5}}>·</span>}
                        {tk.priority&&<span style={{color:tk.priority==="High"?"var(--red)":"var(--g400)",fontWeight:tk.priority==="High"?600:400}}>{tk.priority==="High"?(isRTL?"أولوية عالية":"High priority"):tk.priority==="Low"?(isRTL?"أولوية منخفضة":"Low priority"):(isRTL?"أولوية متوسطة":"Medium priority")}</span>}
                      </div>
                      <div style={{fontSize:11,color:"var(--g400)",marginTop:2}}>{tk.person || (isRTL?"غير مسند":"Unassigned")}{tk.due?` · ${fmtDate(tk.due,lang)}`:""}</div>
                    </div>
                    <span className={`pill ${tk.status==="Done"?"p-done":tk.status==="Waiting"?"p-wait":"p-open"}`} style={{flexShrink:0,fontSize:10}}>{tk.status==="Done"?t.done:tk.status==="Waiting"?t.waiting:t.open}</span>
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" stroke="var(--g400)" strokeWidth="1.5" strokeLinecap="round" style={{flexShrink:0}}><path d="M1 1l5 5-5 5"/></svg>
                  </div>
                  );
                })}
              </div>;
          })()}
          {canEdit&&(
            <div style={{marginTop:20}}>
              <button className="btn btn-p" style={{width:"100%",padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:14}} onClick={()=>setShowAddTask(true)}>+ {isRTL?"مهمة جديدة":"New Task"}</button>
            </div>
          )}
        </div>
      )}

      {/* EXPENSES */}
      {tab==="expenses" && (
        <div>
          {showAddExpense&&(
            <Modal>
              <div className="stf-detail-overlay" onClick={()=>setShowAddExpense(false)} role="dialog" aria-modal="true" aria-label={t.logExpense||"Log Expense"}>
                <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
                  <div className="stf-detail-handle" aria-hidden="true"/>
                  <div className="stf-detail-header">
                    <div style={{minWidth:0,flex:1}}>
                      <div className="stf-detail-title">{t.logExpense||"Log Expense"}</div>
                      <div className="stf-detail-sub">{displayName}</div>
                    </div>
                    <button className="stf-detail-close" onClick={()=>{setExpenseWsId("");setShowAddExpense(false);}} aria-label={t.close||"Close"}>✕</button>
                  </div>
                  <div className="stf-detail-body">
                    {(project.workstreams||[]).length>0&&(
                      <div className="fg" style={{marginBottom:12}}>
                        <label className="fl" htmlFor="exp-new-ws">{isRTL?"مسار العمل":"Workstream"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label>
                        <select id="exp-new-ws" className="fs" value={expenseWsId} onChange={e=>setExpenseWsId(e.target.value)}>
                          <option value="">{isRTL?"— بدون مسار عمل —":"— No workstream —"}</option>
                          {(project.workstreams||[]).filter(w=>w.status!=="completed").map(w=><option key={w.id} value={w.id}>{isRTL&&w.nameAr?w.nameAr:w.name}</option>)}
                        </select>
                      </div>
                    )}
                    <ExpenseForm
                      cats={expenseCats} locations={locations} users={users}
                      vehicles={state.vehicles||[]} projects={state.projects||[]}
                      staff={state.staff||[]}
                      defaultUser={currentUser?.id||"u1"} currentUser={currentUser} t={t} lang={lang}
                      onSave={e=>{
                        dispatch({type:"ADD_EXPENSE",expense:{...e,projectId:project.id,workstreamId:expenseWsId||""}});
                        /* Fire over-budget notification if this expense tips the project past its budget. */
                        if(budget>0){
                          const newTotal=totalCost+(parseFloat(e.amount)||0);
                          if(totalCost<=budget&&newTotal>budget){
                            fireNotification({eventKey:"project_over_budget",state,dispatch,platformCtx,link:"Projects",eventData:{projectName:project.name}});
                          }
                        }
                        setExpenseWsId("");
                        setShowAddExpense(false);
                      }}
                      onCancel={()=>{setExpenseWsId("");setShowAddExpense(false);}}
                      defaultProjectId={project.id}
                    />
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {projectExpenses.length===0
            ? <div className="exp-merged-card" style={{padding:32,textAlign:"center"}}>
                <div style={{fontSize:14,color:"var(--g400)",marginBottom:4}}>{isRTL?"لا مصروفات بعد":"No expenses yet"}</div>
                <div style={{fontSize:12,color:"var(--g400)"}}>{isRTL?"سجل مصروفاً أعلاه أو اربط المصروفات باختيار هذا المشروع في المالية":"Log one above, or link existing expenses by selecting this project in Finance → Expenses."}</div>
              </div>
            : <>
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  {[{l:isRTL?"التكلفة":"Total Cost",v:fmtSAR(totalCost)},{l:isRTL?"السجلات":"Entries",v:projectExpenses.length},{l:isRTL?"موافق عليه":"Approved",v:projectExpenses.filter(e=>e.approved).length}].map(x=>(
                    <div key={x.l} className="exp-merged-card" style={{flex:1,padding:"10px 12px"}}><div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>{x.l}</div><div style={{fontFamily:"var(--fm)",fontSize:15,fontWeight:600}}>{x.v}</div></div>
                  ))}
                </div>
                <div className="exp-merged-card">
                  {projectExpenses.map(e=>{
                    const cat=expenseCats.find(c=>c.id===e.catId);
                    return(
                      <div key={e.id} style={{padding:"12px 16px",borderBottom:"1px solid var(--g100)",display:"flex",alignItems:"center",gap:10}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13.5,fontWeight:500,letterSpacing:"-.01em"}}>{e.notes||e.expName||"—"}</div>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:3}}>
                            {cat&&<span className="pill p-gold" style={{fontSize:9}}>{isRTL&&cat.nameAr?cat.nameAr:cat.name}</span>}
                            {e.approved?<span className="pill p-done" style={{fontSize:9}}>{t.approved||"Approved"}</span>:<span className="pill p-wait" style={{fontSize:9}}>{t.pending||"Pending"}</span>}
                          </div>
                          <div style={{fontSize:11,color:"var(--g400)",marginTop:2}}>{e.date||"—"}</div>
                        </div>
                        <div style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:600,flexShrink:0}}>{fmtSAR(e.amount)}</div>
                      </div>
                    );
                  })}
                  <div style={{padding:"10px 16px",display:"flex",justifyContent:"space-between",background:"var(--g50)"}}>
                    <span style={{fontWeight:600,fontSize:13}}>{isRTL?"الإجمالي":"Total"}</span>
                    <span style={{fontFamily:"var(--fm)",fontSize:14,fontWeight:700}}>{fmtSAR(totalCost)}</span>
                  </div>
                </div>
              </>
          }
          {canEdit&&(
            <div style={{marginTop:20}}>
              <button className="btn btn-p" style={{width:"100%",padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:14}} onClick={()=>setShowAddExpense(true)}>+ {t.logExpense||"Log Expense"}</button>
            </div>
          )}
        </div>
      )}

      {/* DOCUMENTS */}
      {tab==="documents" && (
        <div>
          {projectDocs.length===0
            ? <div className="exp-merged-card" style={{padding:32,textAlign:"center"}}>
                <div style={{fontSize:14,color:"var(--g400)",marginBottom:4}}>{isRTL?"لا مستندات بعد":"No documents yet"}</div>
                <div style={{fontSize:12,color:"var(--g400)"}}>{isRTL?"ارفع ملفات أعلاه":"Upload files above."}</div>
              </div>
            : <div className="exp-merged-card">
                {uploadedDocs.length>0&&(
                  <>
                    <div style={{padding:"8px 16px 4px",fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".08em",background:"var(--g50)",borderBottom:"1px solid var(--g100)"}}>{isRTL?"مرفوع":"Uploaded"}</div>
                    {uploadedDocs.map(doc=>(
                      <div key={doc.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:"1px solid var(--g100)"}}>
                        <div style={{width:32,height:32,borderRadius:8,background:"var(--blu-bg)",border:"1px solid var(--blu-bd)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--blu)" strokeWidth="1.3"><path d="M2 2C2 1.45 2.45 1 3 1h5.29L11 3.71V12c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V2z"/><path d="M8 1v3.5H11"/></svg>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:500,letterSpacing:"-.01em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{doc.name}</div>
                          <div style={{fontSize:11,color:"var(--g400)",display:"flex",gap:6,flexWrap:"wrap",marginTop:2}}>
                            <span className="pill p-blu" style={{fontSize:9,padding:"1px 6px"}}>{doc.tag}</span>
                            <span>{doc.date}</span>
                            {doc.size&&<span>{doc.size}</span>}
                          </div>
                        </div>
                        <div style={{display:"flex",gap:4,flexShrink:0,flexDirection:"column"}}>
                          {(doc.url||doc.dataUrl)&&(
                            <button onClick={()=>authFileDownload(doc.url||doc.dataUrl,doc.fileName||doc.name)} className="btn btn-s btn-sm" style={{cursor:"pointer",fontSize:11,padding:"3px 8px"}}>{t.download||"DL"}</button>
                          )}
                          {canEdit && <button className="btn btn-d btn-sm" style={{fontSize:11,padding:"3px 8px"}} onClick={()=>askConfirm({
                            title:isRTL?"إزالة مستند":"Remove Document",
                            message:isRTL?`سيتم إزالة "${doc.name}" من هذا المشروع.`:`"${doc.name}" will be removed from this project.`,
                            confirmLabel:t.remove||"Remove",
                            onConfirm:()=>dispatch({type:"REMOVE_PROJECT_DOC",projectId:project.id,docId:doc.id}),
                          })}>{t.remove||"Del"}</button>}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {expenseDocs.length>0&&(
                  <>
                    <div style={{padding:"8px 16px 4px",fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".08em",background:"var(--g50)",borderBottom:"1px solid var(--g100)"}}>{isRTL?"مرتبط من المصروفات":"Linked from Expenses"}</div>
                    {expenseDocs.map(doc=>(
                      <div key={doc.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:"1px solid var(--g100)"}}>
                        <div style={{width:32,height:32,borderRadius:8,background:"var(--gold-bg)",border:"1px solid var(--gold-bd)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--amb)" strokeWidth="1.3"><path d="M2 2C2 1.45 2.45 1 3 1h5.29L11 3.71V12c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V2z"/><path d="M8 1v3.5H11"/></svg>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:500,letterSpacing:"-.01em"}}>{doc.name}</div>
                          <div style={{fontSize:11,color:"var(--g400)",display:"flex",gap:6,marginTop:2}}>
                            <span className="pill p-gold" style={{fontSize:9,padding:"1px 6px"}}>{isRTL?"فاتورة":"Invoice"}</span>
                            <span>{doc.sourceLabel}</span>
                            <span>{doc.date}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
          }
          {canEdit&&(
            <div style={{marginTop:20}}>
              <button className="btn btn-p" style={{width:"100%",padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:14}} onClick={()=>setShowAddDoc(true)}>+ {t.uploadDocument||"Upload Document"}</button>
            </div>
          )}
        </div>
      )}

      {/* Add document modal */}
      {showAddDoc && (
        <Modal>
          <div className="stf-detail-overlay" onClick={()=>setShowAddDoc(false)} role="dialog" aria-modal="true" aria-label={t.uploadDocument||"Upload Document"}>
            <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
              <div className="stf-detail-handle" aria-hidden="true"/>
              <div className="stf-detail-header">
                <div style={{minWidth:0,flex:1}}>
                  <div className="stf-detail-title">{t.uploadDocument||"Upload Document"}</div>
                  <div className="stf-detail-sub">{displayName}</div>
                </div>
                <button className="stf-detail-close" onClick={()=>setShowAddDoc(false)} aria-label={t.close||"Close"}>✕</button>
              </div>
              <div className="stf-detail-body">
                <div className="fg" style={{marginBottom:12}}>
                  <label className="fl" htmlFor="doc-new-name">{t.docName||"Document name"}</label>
                  <input id="doc-new-name" className="fi" value={docName} onChange={e=>setDocName(e.target.value)} placeholder={isRTL?"مثال: عقد — المرحلة 1":"e.g. Contract — Phase 1"}/>
                </div>
                <div className="fg" style={{marginBottom:12,position:"relative"}}>
                  <label className="fl" htmlFor="doc-new-tag">{t.docTag||"Tag"}</label>
                  <input id="doc-new-tag" className="fi" value={docTag}
                    onChange={e=>{setDocTag(e.target.value);setDocTagSuggestions((state.docTags||[]).filter(tg=>tg.toLowerCase().includes(e.target.value.toLowerCase())));setShowDocTagDrop(e.target.value.length>0);}}
                    onFocus={()=>{setDocTagSuggestions(state.docTags||[]);setShowDocTagDrop(true);}}
                    onBlur={()=>setTimeout(()=>setShowDocTagDrop(false),150)}
                    placeholder={isRTL?"اكتب أو اختر…":"Type or choose…"}/>
                  {showDocTagDrop&&docTagSuggestions.length>0&&(
                    <div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"var(--r)",boxShadow:"var(--sh2)",zIndex:300,maxHeight:140,overflowY:"auto"}}>
                      {docTagSuggestions.map(tag=>(
                        <div key={tag} onMouseDown={()=>{setDocTag(tag);setShowDocTagDrop(false);}} style={{padding:"7px 12px",fontSize:13,cursor:"pointer",borderBottom:"1px solid var(--g100)"}}
                          onMouseEnter={e=>e.currentTarget.style.background="var(--g50)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          {tag}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="fg" style={{marginBottom:4}}>
                  <label className="fl">{t.chooseFile||"File"}</label>
                  <label style={{display:"block",cursor:"pointer"}}>
                    <div className={`upz ${docFile?"filled":""}`} style={{minHeight:42,padding:"10px 14px",display:"flex",alignItems:"center",gap:8,marginBottom:0}}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M7 1v9M4 4l3-3 3 3M1 11h12"/></svg>
                      <span style={{fontSize:13,color:docFile?"var(--grn)":"var(--g400)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {docFile?docFile.name:(t.chooseFile||"Choose file")}
                      </span>
                    </div>
                    <input ref={docInputRef} type="file" style={{display:"none"}}
                      onChange={e=>{ const f=e.target.files[0]; if(f)setDocFile(f); }}/>
                  </label>
                </div>
              </div>
              <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{setShowAddDoc(false);setDocName("");setDocFile(null);setDocTag("");if(docInputRef.current)docInputRef.current.value="";}}>{t.cancel||"Cancel"}</button>
                <button className="btn btn-p" disabled={!docFile} style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",opacity:docFile?1:0.5}}
                  onClick={async()=>{
                    if(!docFile)return;
                    const label=docName.trim()||docFile.name;
                    const url=await uploadFileToStorage(docFile);
                    dispatch({type:"ADD_PROJECT_DOC",projectId:project.id,doc:{
                      id:_projId("doc"),
                      name:label,
                      fileName:docFile.name,
                      tag:docTag,
                      date:TODAY,
                      size:`${(docFile.size/1024).toFixed(0)} KB`,
                      url:url||"",
                      source:"Upload",
                      sourceLabel:isRTL?"رفع مباشر":"Direct upload",
                    }});
                    setDocName("");setDocFile(null);setDocTag("");
                    if(docInputRef.current)docInputRef.current.value="";
                    setShowAddDoc(false);
                  }}>
                  {t.upload||"Upload"}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* PEOPLE */}
      {tab==="people" && (()=>{
        const existingUserIds=new Set((project.people||[]).filter(pp=>pp.type==="user").map(pp=>pp.userId));
        const availableUsers=(users||[]).filter(u=>u.active&&!existingUserIds.has(u.id));
        const userMembers=(project.people||[]).filter(pp=>pp.type==="user");
        const externalMembers=(project.people||[]).filter(pp=>pp.type!=="user");
        const addPerson=()=>{
          let memberName="";
          if(personMode==="user"){
            const m=validateFields([{ok:personForm.userId,label:isRTL?"المستخدم":"User"}]);
            if(m.length){vt.show(m.join(", "));return;}
            const u=users.find(x=>x.id===personForm.userId);
            if(!u)return;
            memberName=u.name;
            dispatch({type:"ADD_PROJECT_PERSON",projectId:project.id,person:{
              id:_projId("pp"),type:"user",userId:u.id,name:u.name,
              nameAr:u.nameAr||"",role:personForm.role.trim(),initials:u.initials||u.name.slice(0,2).toUpperCase(),
            }});
          }else{
            const m=validateFields([{ok:personForm.name.trim(),label:isRTL?"الاسم":"Name"}]);
            if(m.length){vt.show(m.join(", "));return;}
            memberName=personForm.name.trim();
            dispatch({type:"ADD_PROJECT_PERSON",projectId:project.id,person:{
              id:_projId("pp"),type:"external",name:memberName,role:personForm.role.trim(),
            }});
          }
          fireNotification({eventKey:"project_member_added",state,dispatch,platformCtx,link:"Projects",eventData:{projectName:project.name,memberName}});
          setPersonForm({name:"",role:"",userId:""});setShowAddPerson(false);
        };
        return(
        <div>

          {showAddPerson && (
            <Modal>
              <div className="stf-detail-overlay" onClick={()=>{setShowAddPerson(false);setPersonMode("user");}} role="dialog" aria-modal="true" aria-label={isRTL?"إضافة شخص":"Add Person"}>
                <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
                  <div className="stf-detail-handle" aria-hidden="true"/>
                  <div className="stf-detail-header">
                    <div style={{minWidth:0,flex:1}}>
                      <div className="stf-detail-title">{isRTL?"إضافة شخص":"Add Person"}</div>
                      <div className="stf-detail-sub">{displayName}</div>
                    </div>
                    <button className="stf-detail-close" onClick={()=>{setShowAddPerson(false);setPersonMode("user");}} aria-label={t.close||"Close"}>✕</button>
                  </div>
                  <div className="stf-detail-body">
                    {/* Mode toggle */}
                    <div style={{display:"flex",gap:8,marginBottom:14}}>
                      <button type="button" className={`btn btn-sm ${personMode==="user"?"btn-p":"btn-g"}`} onClick={()=>setPersonMode("user")}>{isRTL?"مستخدم الورك سبيس":"Workspace User"}</button>
                      <button type="button" className={`btn btn-sm ${personMode==="external"?"btn-p":"btn-g"}`} onClick={()=>setPersonMode("external")}>{isRTL?"شخص خارجي":"External Contact"}</button>
                    </div>
                    {personMode==="user"?(
                      <div>
                        <div className="fg" style={{marginBottom:10}}><label className="fl" htmlFor="prj-add-person-user">{isRTL?"اختر مستخدم":"Select User"}</label>
                          <select id="prj-add-person-user" className="fs" value={personForm.userId} onChange={e=>setPersonForm(p=>({...p,userId:e.target.value}))}>
                            <option value="">{isRTL?"— اختر —":"— Select —"}</option>
                            {availableUsers.map(u=><option key={u.id} value={u.id}>{isRTL&&u.nameAr?u.nameAr:u.name} ({_fmtRole(u.role)})</option>)}
                          </select>
                        </div>
                        <div className="fg" style={{marginBottom:10}}><label className="fl" htmlFor="prj-add-person-role">{isRTL?"الدور في المشروع":"Project Role"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label>
                          <input id="prj-add-person-role" className="fi" value={personForm.role} onChange={e=>setPersonForm(p=>({...p,role:e.target.value}))} placeholder={isRTL?"مثال: مدير المشروع":"e.g. Project Lead"}/>
                        </div>
                        <div style={{fontSize:11,color:"var(--g400)"}}>{isRTL?"المستخدمون المضافون سيتمكنون من الوصول لهذا المشروع فقط.":"Added users will have access to this project. Other users won't see it."}</div>
                      </div>
                    ):(
                      <div>
                        <div className="fr">
                          <div className="fg" style={{flex:2}}><label className="fl" htmlFor="prj-add-ext-name">{isRTL?"الاسم":"Name"}</label><input id="prj-add-ext-name" className="fi" value={personForm.name} onChange={e=>setPersonForm(p=>({...p,name:e.target.value}))} placeholder={isRTL?"مثال: أحمد الفيصل":"e.g. Ahmed Al-Faisal"} autoFocus/></div>
                          <div className="fg" style={{flex:1}}><label className="fl" htmlFor="prj-add-ext-role">{t.role||"Role"} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional||"optional"})</span></label><input id="prj-add-ext-role" className="fi" value={personForm.role} onChange={e=>setPersonForm(p=>({...p,role:e.target.value}))} placeholder={isRTL?"مثال: مقاول":"e.g. Contractor"}/></div>
                        </div>
                        <div style={{fontSize:11,color:"var(--g400)",marginTop:10}}>{isRTL?"جهات اتصال خارجية تظهر أسماؤهم فقط بدون وصول للنظام.":"External contacts appear by name only — no system access."}</div>
                      </div>
                    )}
                  </div>
                  <div style={{padding:"12px 20px 24px",display:"flex",gap:8}}>
                    <button className="btn btn-g" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{setShowAddPerson(false);setPersonMode("user");}}>{t.cancel||"Cancel"}</button>
                    <button className="btn btn-p" style={{flex:1,borderRadius:14,padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={addPerson}>{t.add||"Add"}</button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {(project.people||[]).length===0
            ? <div className="exp-merged-card" style={{padding:32,textAlign:"center"}}>
                <div style={{fontSize:14,color:"var(--g400)",marginBottom:6}}>{isRTL?"لم يُضَف أشخاص":"No people assigned"}</div>
                <div style={{fontSize:12,color:"var(--g400)"}}>{isRTL?"أضف مستخدمين للتحكم بالوصول، أو جهات خارجية للعرض فقط.":"Add workspace users to control access, or external contacts for display only."}</div>
              </div>
            : <div className="exp-merged-card">
                {/* Workspace users section */}
                {userMembers.length>0&&(
                  <>
                    <div style={{padding:"8px 16px 4px",fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".08em",background:"var(--g50)",borderBottom:"1px solid var(--g100)"}}>{isRTL?"مستخدمو الورك سبيس":"Workspace Users"} ({userMembers.length})</div>
                    {userMembers.map(pp=>{
                      const wsUser=(users||[]).find(u=>u.id===pp.userId);
                      return(
                        <div key={pp.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderBottom:"1px solid var(--g100)"}}>
                          <div style={{width:36,height:36,borderRadius:"50%",background:wsUser?"var(--ink)":"var(--g100)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:wsUser?"white":"var(--g600)",flexShrink:0}}>{pp.initials||pp.name.slice(0,2).toUpperCase()}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:14,fontWeight:500,letterSpacing:"-.01em"}}>{isRTL&&pp.nameAr?pp.nameAr:pp.name}</div>
                            <div style={{display:"flex",gap:6,alignItems:"center",marginTop:2}}>
                              {pp.role&&<span style={{fontSize:12,color:"var(--g400)"}}>{pp.role}</span>}
                              <span className={`pill ${wsUser?.active?"p-done":"p-low"}`} style={{fontSize:9}}>{wsUser?.active?(isRTL?"نشط":"Active"):(isRTL?"غير نشط":"Inactive")}</span>
                              <span className="pill p-blu" style={{fontSize:9}}>{isRTL?"مستخدم":"User"}</span>
                            </div>
                          </div>
                          {canEdit && <button className="btn btn-d btn-sm" onClick={()=>askConfirm({
                            title:isRTL?"إزالة مستخدم":"Remove User",
                            message:isRTL?`سيفقد ${pp.name} الوصول إلى هذا المشروع.`:`${pp.name} will lose access to this project.`,
                            confirmLabel:t.remove||"Remove",
                            onConfirm:()=>{
                              dispatch({type:"REMOVE_PROJECT_PERSON",projectId:project.id,personId:pp.id});
                              fireNotification({eventKey:"project_member_removed",state,dispatch,platformCtx,link:"Projects",eventData:{projectName:project.name,memberName:pp.name}});
                            },
                          })}>{t.remove||"Remove"}</button>}
                        </div>
                      );
                    })}
                  </>
                )}
                {/* External contacts section */}
                {externalMembers.length>0&&(
                  <>
                    <div style={{padding:"8px 16px 4px",fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".08em",background:"var(--g50)",borderBottom:"1px solid var(--g100)"}}>{isRTL?"جهات خارجية":"External Contacts"} ({externalMembers.length})</div>
                    {externalMembers.map(pp=>(
                      <div key={pp.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderBottom:"1px solid var(--g100)"}}>
                        <div style={{width:36,height:36,borderRadius:"50%",background:"var(--g100)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:"var(--g600)",flexShrink:0}}>{pp.name.slice(0,2).toUpperCase()}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:14,fontWeight:500,letterSpacing:"-.01em"}}>{pp.name}</div>
                          <div style={{display:"flex",gap:6,alignItems:"center",marginTop:2}}>
                            {pp.role&&<span style={{fontSize:12,color:"var(--g400)"}}>{pp.role}</span>}
                            <span className="pill p-gold" style={{fontSize:9}}>{isRTL?"خارجي":"External"}</span>
                          </div>
                        </div>
                        {canEdit && <button className="btn btn-d btn-sm" onClick={()=>askConfirm({
                          title:isRTL?"إزالة جهة خارجية":"Remove External Contact",
                          message:isRTL?`سيتم إزالة ${pp.name} من المشروع.`:`${pp.name} will be removed from this project.`,
                          confirmLabel:t.remove||"Remove",
                          onConfirm:()=>{
                            dispatch({type:"REMOVE_PROJECT_PERSON",projectId:project.id,personId:pp.id});
                            fireNotification({eventKey:"project_member_removed",state,dispatch,platformCtx,link:"Projects",eventData:{projectName:project.name,memberName:pp.name}});
                          },
                        })}>{t.remove||"Remove"}</button>}
                      </div>
                    ))}
                  </>
                )}
              </div>
          }
          {canEdit&&(
            <div style={{marginTop:20}}>
              <button className="btn btn-p" style={{width:"100%",padding:"14px 8px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:14}} onClick={()=>setShowAddPerson(true)}>+ {isRTL?"إضافة شخص":"Add Person"}</button>
            </div>
          )}
        </div>
        );
      })()}
      </div>

      {/* Task detail sheet (opens on row click) */}
      {detailTask && <TaskDetailModal state={state} dispatch={dispatch} t={t} task={detailTask} onClose={()=>setDetailTask(null)} onUpdate={tk=>setDetailTask(tk)} canEdit={canEditTask(detailTask,currentUser,state.taskVisibilityRules||{},users,state.projects||[])}/>}
    </>
  );
}

export { PROJECT_STATUS_COLORS, ProjectList, ProjectCard, ProjectDetail };
export default ProjectsModule;
